import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {Vector2, Vector2Like} from "@cm/math"
import {AStarPathFinding} from "@app/textures/texture-editor/operator-stack/operators/tiling/helpers/a-star-path-finding"
import {FloodFill} from "@app/textures/texture-editor/operator-stack/operators/tiling/helpers/flood-fill"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/generate-border-blend-mask/definition"
import {assertSameDescriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ImageOpNodeConvertColorSpace} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convert-color-space/node"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    ImageOpOutputs,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpNodeGaussianBlur} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/gaussian-blur/node"
import {isImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {BorderDirection} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/extract-border/definition"
import {z} from "zod"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"

@registerNode
export class ImageOpNodeGenerateBorderBlendMask extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeGenerateBorderBlendMask"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {highBorder, lowBorder, borderDirection} = await evaluator.getAll(this.parameters)

        let backgroundImage: ValueOrNode<AnyImageOpResult> = highBorder
        let foregroundImage: ValueOrNode<AnyImageOpResult> = lowBorder

        assertSameDescriptor(backgroundImage.descriptor, foregroundImage.descriptor)
        const descriptor = highBorder.descriptor

        // if RGB convert to CIE-LAB
        if (backgroundImage.descriptor.channels !== 1) {
            backgroundImage = imageOpNodeBag.getNode("backgroundToLab", ImageOpNodeConvertColorSpace, {
                sourceImage: backgroundImage,
                sourceColorSpace: "linear",
                targetColorSpace: "lab",
            })
            foregroundImage = imageOpNodeBag.getNode("foregroundToLab", ImageOpNodeConvertColorSpace, {
                sourceImage: foregroundImage,
                sourceColorSpace: "linear",
                targetColorSpace: "lab",
            })
        }

        // use perceptual difference between foreground and background image as cost
        const costImage = imageOpNodeBag.getNode("distance", ImageOpNodeMath, {
            operator: "distance",
            operandA: backgroundImage,
            operandB: foregroundImage,
        })

        // construct mask image by finding the path of the least cost
        const computedMask = imageOpNodeBag.getLambda("generateMask", {costImage}, ({costImage}) => this.generateMask(costImage, borderDirection))
        const maskImage = imageOpNodeBag.getNode("createMask", ImageOpNodeCreateImage, {
            descriptor: {
                width: descriptor.width,
                height: descriptor.height,
                channels: 1,
            },
            data: computedMask,
        })

        // blur the mask image to smooth the edges
        const result = imageOpNodeBag.getNode("blur", ImageOpNodeGaussianBlur, {
            sourceImage: maskImage,
            sigma: 1,
            borderMode: "renormalize",
        })

        return await evaluator.get(result)
    }

    private async generateMask(costImage: AnyImageOpResult, direction: z.infer<typeof BorderDirection>) {
        if (!isImageOpHal(costImage)) {
            throw new Error("costImage is not a HalImage")
        }
        if (costImage.descriptor.channels !== 1) {
            throw new Error("costImage must be a single channel image")
        }
        // we perform A* search to find the path of least perceptual difference using a CPU implementation for now
        const halImage = await costImage.getUpdatedHalImage({
            x: 0,
            y: 0,
            width: costImage.descriptor.width,
            height: costImage.descriptor.height,
            mipLevel: 0,
        })
        if (!halImage) {
            throw new Error("costImage could not be updated")
        }
        const costData = halImage.halImage.readImageDataFloat()
        halImage.release()
        // fill border boundary with high cost to avoid getting too near it
        const borderCost = 1000000
        const borderWidthRatio = 0.2
        let offsetPixel, offsetLine, numPixels, numLines, offsetOtherBoundary: number
        if (direction === "horizontal") {
            offsetPixel = 1
            offsetLine = costImage.descriptor.width
            numPixels = costImage.descriptor.width
            numLines = Math.ceil(costImage.descriptor.height * borderWidthRatio)
            offsetOtherBoundary = (costImage.descriptor.height - numLines) * costImage.descriptor.width
        } else {
            offsetPixel = costImage.descriptor.width
            offsetLine = 1
            numPixels = costImage.descriptor.height
            numLines = Math.ceil(costImage.descriptor.width * borderWidthRatio)
            offsetOtherBoundary = costImage.descriptor.width - numLines
        }
        let jPos = 0
        for (let j = 0; j < numLines; j++) {
            let iPos = jPos
            for (let i = 0; i < numPixels; i++) {
                costData[iPos] = borderCost
                costData[iPos + offsetOtherBoundary] = borderCost
                iPos += offsetPixel
            }
            jPos += offsetLine
        }
        // determine start and end pixel
        let startPixel, endPixel: Vector2
        if (direction === "horizontal") {
            startPixel = new Vector2(0, Math.floor(costImage.descriptor.height / 2))
            endPixel = new Vector2(costImage.descriptor.width - 1, Math.floor(costImage.descriptor.height / 2))
        } else {
            startPixel = new Vector2(Math.floor(costImage.descriptor.width / 2), 0)
            endPixel = new Vector2(Math.floor(costImage.descriptor.width / 2), costImage.descriptor.height - 1)
        }
        // find path
        const pathFindingStartTime = performance.now()
        const tupleToIndex = (p: Vector2Like) => p.y * costImage.descriptor.width + p.x
        const indexToTuple = (index: number) => new Vector2(Math.floor(index % costImage.descriptor.width), Math.floor(index / costImage.descriptor.width))
        const distance = (from: Vector2Like, to: Vector2Like) => Math.max(Math.abs(from.x - to.x), Math.abs(from.y - to.y)) // chebyshev distance due to 8-connectivity
        const path = AStarPathFinding.findPath(
            costData.length,
            tupleToIndex(startPixel),
            tupleToIndex(endPixel),
            (from: number, to: number) => distance(indexToTuple(from), indexToTuple(to)),
            (index: number) => {
                const tuple = indexToTuple(index)
                const neighbors: number[] = []
                for (let y = tuple.y - 1; y <= tuple.y + 1; y++) {
                    for (let x = tuple.x - 1; x <= tuple.x + 1; x++) {
                        if (x === tuple.x && y === tuple.y) {
                            continue
                        }
                        if (x < 0 || x >= costImage.descriptor.width || y < 0 || y >= costImage.descriptor.height) {
                            continue
                        }
                        neighbors.push(tupleToIndex({x, y}))
                    }
                }
                return neighbors
            },
            (from: number, to: number) => {
                const costWeight = 500 // weight of cost in path finding; scaled for Lab now normalized to 0-1 (was 5 for 0-100 Lab)
                return distance(indexToTuple(from), indexToTuple(to)) + (costData[to] + costData[from]) * costWeight
            },
        )
        const pathFindingEndTime = performance.now()
        ImageOpNodeGenerateBorderBlendMask.logger?.debug("path finding took", pathFindingEndTime - pathFindingStartTime, "ms")
        ImageOpNodeGenerateBorderBlendMask.logger?.debug("path", path)

        const fillStartTime = performance.now()
        const maskData = new Float32Array(costImage.descriptor.width * costImage.descriptor.height).fill(1)
        path.forEach((index) => {
            maskData[index] = 0
        })
        let fillStartPos: Vector2
        if (direction === "horizontal") {
            fillStartPos = new Vector2(Math.floor(costImage.descriptor.width / 2), 0)
        } else {
            fillStartPos = new Vector2(0, Math.floor(costImage.descriptor.height / 2))
        }
        FloodFill.fill(maskData, costImage.descriptor, fillStartPos, 0)
        const fillEndTime = performance.now()
        ImageOpNodeGenerateBorderBlendMask.logger?.debug("filling took", fillEndTime - fillStartTime, "ms")

        return maskData
    }
}
